import { useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  PersistQueryClientProvider,
  type PersistedClient,
} from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

import {
  createQueryClient,
  QUERY_CACHE_STORAGE_KEY,
  QUERY_CACHE_VERSION,
  sollGespiegeltWerden,
} from "./client";
import {
  acceptRestoredCacheIdentity,
  currentCacheIdentity,
} from "./cacheIdentity";

// ---------------------------------------------------------------------------
// QueryProvider — TanStack Query mit Persist (Session 16, Tab-Wake-Flash-Fix).
// ---------------------------------------------------------------------------
// Ohne Persist: jeder Tab-Wake / Page-Reload liess gecachte Listen leer
// zurueckkommen, jeder Hook re-fetched, „Laden..."-Skeletons ueberall.
// Mit Persist: Listen leben in localStorage, beim Wake sofort sichtbar,
// Hintergrund-Refetch (staleTime abgelaufen) ersetzt sie still.

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

// STABIL-13 — die Kennung reist IM Spiegel.
//
// Der Spiegel liegt im localStorage und gilt damit fuer ALLE Tabs; wer
// zuletzt schreibt, gewinnt den Platz. Frueher stand daneben ein zweiter
// Schluessel mit der Kennung, geschrieben von einem anderen Mechanismus zu
// einem anderen Zeitpunkt. Bei zwei offenen Tabs konnten die beiden von
// verschiedenen Personen stammen — dann trug ein fremder Stand die eigene
// Kennung und ueberlebte jedes Neuladen.
//
// Jetzt werden Daten und Kennung in EINEM Schreibvorgang abgelegt. Ein
// Spiegel kann ueber seine Herkunft nicht mehr luegen: wer ihn liest,
// vergleicht die mitgereiste Kennung mit der eigenen Person und verwirft ihn,
// wenn sie nicht passt (`adoptCacheIdentity`).
interface IdentityStampedClient {
  identity?: string;
}

const persister =
  typeof window !== "undefined"
    ? createSyncStoragePersister({
        storage: window.localStorage,
        key: QUERY_CACHE_STORAGE_KEY,
        // throttleTime sammelt mehrere Cache-Aenderungen in einem Write —
        // localStorage-IO ist sync und blockiert sonst den Main-Thread.
        throttleTime: 1000,
        serialize: (client) => {
          const identity = currentCacheIdentity();
          // Ist die Person unbekannt (`undefined`), bleibt das Feld weg —
          // `JSON.stringify` laesst undefined fallen. Der naechste Lauf liest
          // einen Spiegel ohne Herkunft und verwirft ihn. Fail-safe.
          const stamped: PersistedClient & IdentityStampedClient = {
            ...client,
            ...(typeof identity === "string" ? { identity } : {}),
          };
          return JSON.stringify(stamped);
        },
        deserialize: (raw) => {
          const parsed = JSON.parse(raw) as PersistedClient &
            IdentityStampedClient;
          // VTR-7 — hier wird der Spiegel GEPRUEFT, nicht bloss zur Kenntnis
          // genommen. Vorher hat diese Stelle den Stempel uebernommen und den
          // Inhalt in jedem Fall hydriert; widerlegen konnte ihn erst
          // `/api/me`, eine Rundreise spaeter. Genau in dieser Rundreise stand
          // Daniels eigene Karten-Liste unter Marvins Namen auf dem Schirm.
          //
          // Die Vertretung dieses Tabs ist synchron bekannt. Passt der Stempel
          // nicht zu ihr, faellt der Inhalt hier weg — vor dem ersten Bild.
          // Der Spiegel im localStorage bleibt vorerst liegen; der erste
          // `adoptCacheIdentity` raeumt ihn weg (nach einer Zurueckweisung ist
          // die Kennung `undefined`, also raeumt er in jedem Fall).
          if (!acceptRestoredCacheIdentity(parsed.identity)) {
            return { ...parsed, clientState: { mutations: [], queries: [] } };
          }
          return parsed;
        },
      })
    : undefined;

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);

  if (!persister) {
    // SSR-Pfad oder Test-Umgebung ohne window — fallback ohne Persist, aber
    // MIT Client: alles darunter (jeder Hook, auch der AuthContext) erwartet
    // einen. Ohne Provider warf dieser Zweig beim ersten Zugriff.
    return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      client={client}
      persistOptions={{
        persister,
        maxAge: ONE_DAY_MS,
        // Buster invalidiert den gesamten persistierten Cache, wenn sich
        // unsere Cache-Schema-Version aendert (z.B. nach API-Breaking-Change).
        buster: QUERY_CACHE_VERSION,
        // Session 16b: Nur erfolgreiche Queries persistieren. Ohne diesen
        // Filter landeten auch Error-/Pending-States im localStorage — beim
        // Rehydrate fuehrt das zu teilbefuellten Query-States, die Components
        // mit `data` Annahmen zum Crash bringen (Projekte-Blackscreen).
        // FRAGEN-1: eine Abfrage mit `meta: { spiegel: false }` bleibt
        // draussen — ihr Inhalt (ein Fragebogen kann einen Lohn nennen)
        // gehoert nicht in den localStorage, der allen Tabs und jedem
        // gehoert, der danach am Geraet sitzt.
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => sollGespiegeltWerden(q.state.status, q.meta),
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
